import { SOUND_EVENTS } from './registry';

// Called only while holding the account/browser Web Lock. Storage holds delivery
// identities only, never authoritative preferences, cursors, tokens or event payloads.
export class AudioDelivery {
  constructor({ scope, player, preferences, storage,
    current = () => true, now = Date.now, status = () => {} }) {
    Object.assign(this, { scope, player, preferences, storage, current, now, status });
    this.queue = [];
    this.busy = false;
    this.disposed = false;
    this.key = `dop:sound-delivered:${encodeURIComponent(scope)}`;
  }
  accept(event) {
    if (this.disposed || !this.current() || !SOUND_EVENTS.some((e) => e.id === event.type)) return;
    const identity = JSON.stringify([this.scope, event.type, event.id]);
    try {
      const storage = this.storage || window.localStorage;
      const raw = storage.getItem(this.key);
      const stored = raw ? JSON.parse(raw) : [];
      if (!Array.isArray(stored) || stored.some((row) =>
        !row || typeof row.id !== 'string' || !Number.isFinite(row.at))) throw new Error('Invalid dedup data');
      const recent = stored.filter((row) => this.now() - row.at < 60000);
      if (recent.some((row) => row.id === identity)) return;
      // Never evict potentially live IDs and then replay them during a large burst.
      if (recent.length >= 2000) {
        this.status('dedup-full');
        return;
      }
      storage.setItem(this.key, JSON.stringify([...recent, { id: identity, at: this.now() }]));
    } catch (_) {
      this.status('coordination-unavailable');
      return; // Fail closed rather than double-play across tabs.
    }
    const preferences = this.preferences();
    if (!preferences.masterEnabled || !preferences.events[event.type]?.enabled ||
        !this.player.activated || this.queue.length >= 3) return;
    this.queue.push({ event, at: this.now() });
    this.drain();
  }
  async drain() {
    if (this.busy) return;
    this.busy = true;
    try {
      while (!this.disposed && this.current() && this.queue.length) {
        const { event, at } = this.queue.shift();
        const preferences = this.preferences();
        if (this.now() - at > 3000 || !Number.isFinite(event.expiresAt) ||
            this.now() >= event.expiresAt || !preferences.masterEnabled ||
            !preferences.events[event.type]?.enabled) continue;
        if (!(await this.player.play(preferences.events[event.type].chime, preferences.volume))) {
          this.queue = [];
          break;
        }
      }
    } catch (_) {
      this.clear();
      this.status('blocked');
    } finally { this.busy = false; }
  }
  clear() { this.queue = []; this.player.stop(); }
  dispose() { this.disposed = true; this.clear(); }
}

// Web Locks provide atomic exclusion: no racy localStorage lease. No live audio
// on browsers without Web Locks. A suspended leader may delay availability but
// never permits a second tab to steal a still-held lock and double-play.
export function electAudioTab({ accountId, locks = navigator.locks, start, status = () => {} }) {
  if (!locks) { status('coordination-unavailable'); return () => {}; }
  const controller = new AbortController();
  let release;
  let cleanup;
  let stopped = false;
  locks.request(`dop:sound-leader:${accountId}`, { signal: controller.signal }, async () => {
    if (stopped) return;
    await new Promise((resolve) => {
      release = resolve;
      cleanup = start(); // New leader MUST bootstrap; never inherit a stale cursor.
    });
    if (cleanup) { cleanup(); cleanup = null; }
  }).catch((error) => {
    if (error.name !== 'AbortError') status('coordination-unavailable');
  });
  return () => {
    stopped = true;
    if (cleanup) { cleanup(); cleanup = null; }
    controller.abort();
    if (release) release();
  };
}
